import { memoize } from 'lodash-es';
import {
  Locale,
  IntlNumberFormatOptions,
  IntlCurrencyFormatOptions,
} from './types';

const NUMBER_DEFAULT: IntlNumberFormatOptions = {};

const CURRENCY_DEFAULT: IntlCurrencyFormatOptions = {
  style: 'currency',
  currencyDisplay: 'code',
  withCurrency: false,
  inCents: false,
};

const UNIT_DEFAULT: IntlNumberFormatOptions = {
  style: 'unit',
  // @ts-ignore
  unitDisplay: 'long',
};

export const NumberFormats = {
  NUMBER_DEFAULT,
  NUMBER_NO_FRACTION: {
    ...NUMBER_DEFAULT,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
  CURRENCY_DEFAULT,
  CURRENCY_WITH_CURRENCY: {
    ...CURRENCY_DEFAULT,
    withCurrency: true,
  } as IntlCurrencyFormatOptions,
  CURRENCY_IN_CENTS: {
    ...CURRENCY_DEFAULT,
    inCents: true,
  } as IntlCurrencyFormatOptions,
  CURRENCY_WITH_CURRENCY_IN_CENTS: {
    ...CURRENCY_DEFAULT,
    withCurrency: true,
    inCents: true,
  } as IntlCurrencyFormatOptions,
  WITH_CURRENCY_NO_FRACTION: {
    ...CURRENCY_DEFAULT,
    withCurrency: true,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  } as IntlCurrencyFormatOptions,
  UNIT_DEFAULT,
};

const CURRENCY_REMOVAL_RE = /(?:^[^\d]+)|(?:[^\d]+$)/gi;

export default function createNumberFormatters(locale: Locale) {
  const getFormatter = memoize(
    (options: IntlNumberFormatOptions) =>
      new Intl.NumberFormat(locale, options),
    options => JSON.stringify(options, Object.keys(options).sort()),
  );

  function formatNumber(
    value: number,
    options: IntlNumberFormatOptions = NumberFormats.NUMBER_DEFAULT,
  ) {
    return getFormatter(options).format(value);
  }

  function formatCurrency(
    value: number,
    currency: string,
    options: IntlCurrencyFormatOptions = NumberFormats.CURRENCY_DEFAULT,
  ) {
    const { inCents, withCurrency, ...intlOptions } = {
      ...NumberFormats.CURRENCY_DEFAULT,
      ...options,
      style: 'currency',
      currency,
    };

    let result = getFormatter(intlOptions).format(
      inCents ? value / 100 : value,
    );

    if (!withCurrency) {
      result = result.replace(CURRENCY_REMOVAL_RE, '');
    }

    return result;
  }

  function formatUnit(
    value: number,
    unit: string,
    options: IntlNumberFormatOptions = NumberFormats.UNIT_DEFAULT,
  ) {
    const intlOptions = {
      ...NumberFormats.UNIT_DEFAULT,
      ...options,
      style: 'unit',
      unit,
    };

    return getFormatter(intlOptions).format(value);
  }

  return {
    formatNumber,
    formatCurrency,
    formatUnit,
  };
}
